import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { uploadFile } from '../services/storageService';
import { onAuthStateChange } from '../auth';
import { submitApkForAnalysis, triggerApkAnalysis } from '../services/analysisUIService';
import { addDocument, queryDocuments, updateDocument, deleteDocument, getDocument } from '../services/dbService';
import { getAllowedFileTypes, isFileTypeAllowed, initializeSystemSettings } from '../services/systemSettingsService';
import { auth } from '../config/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import AnalysisProgress from './AnalysisProgress';
import Button from './Button';
import { AppStatus } from '../constants/appStatus';
import { updateAppStatus } from '../services/appStatusService';
import { trackFileUpload, trackAnalysisComplete } from '../services/analyticsService';
import { serverTimestamp } from 'firebase/firestore';

export default function ApkDropHero({ hideInstructions = false }) {
  const [dragOver, setDragOver] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [startingAnalysis, setStartingAnalysis] = useState(false);
  const [progress, setProgress] = useState(0);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [fileId, setFileId] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState('');
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [allowedFileTypes, setAllowedFileTypes] = useState([]);
  const inputRef = useRef();
  const uploadTaskRef = useRef(null);
  const navigate = useNavigate();

  // Function to reset the UI state of the component
  const resetApkDropUI = () => {
    setDragOver(false);
    setSuccess(false);
    setError('');
    setUploading(false);
    setStartingAnalysis(false);
    setProgress(0);
    setDownloadUrl('');
    setFileId('');
    setFileName('');
    setFileSize('');
    setShowAnalysis(false);
    setAnalysis(null);
    if (inputRef.current) {
      inputRef.current.value = null; // Clear the file input
    }
    // Note: We are not clearing sessionStorage here, as it's handled by specific logic elsewhere
  };

  // Initialize and load system settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Initialize default settings if they don't exist
        await initializeSystemSettings();
        
        // Load allowed file types
        const fileTypes = await getAllowedFileTypes();
        setAllowedFileTypes(fileTypes);
      } catch (error) {
        console.error('Error loading system settings:', error);
        // Use default settings if there's an error
        setAllowedFileTypes([
          {
            extension: '.aab',
            displayName: 'Android App Bundle',
            enabled: true
          }
        ]);
      }
    };
    
    loadSettings();
  }, []);

  // Check authentication status
  useEffect(() => {
    const unsubscribe = onAuthStateChange((user) => {
      setIsLoggedIn(!!user);
      
      // If user is now logged in, check for saved file data
      if (user) {
        const savedFileData = sessionStorage.getItem('apkFileData');
        if (savedFileData) {
          const fileData = JSON.parse(savedFileData);
          
          // Link anonymous analysis to this user account in userApps collection
          const linkAnonymousApkToUser = async () => {
            try {
              // First check if this file is already linked to the user
              const userApps = await queryDocuments('userApps', 'fileId', '==', fileData.fileId);
              
              // If the app is not yet linked to this user
              if (!userApps || userApps.length === 0) {
                // Get the analysis data to check status
                const analysisRef = doc(db, 'apkAnalysisUIProgress', fileData.fileId);
                const analysisDoc = await getDocument('apkAnalysisUIProgress', fileData.fileId);
                
                // Create current timestamp for consistent usage
                const currentDate = new Date();
                
                // Always create a new document. After analysis completes, the cloud function
                // will detect same Application ID (packageName) and merge versions automatically.
                await addDocument('userApps', {
                  fileId: fileData.fileId,
                  fileName: fileData.fileName,
                  fileSize: fileData.fileSize,
                  downloadUrl: fileData.downloadUrl,
                  userId: user.uid,
                  uploadedAt: currentDate,
                  statusApp: AppStatus.ANALYZING_APK,
                  lastStatusUpdate: currentDate,
                  versionCount: 1,
                  versionFileIds: [fileData.fileId],
                  versions: [{
                    fileId: fileData.fileId,
                    fileSize: fileData.fileSize,
                    downloadUrl: fileData.downloadUrl,
                    uploadedAt: currentDate.toISOString(),
                    versionNumber: 1,
                    statusApp: AppStatus.ANALYZING_APK
                  }],
                  statusHistory: [
                    {
                      status: AppStatus.AWAITING_UPLOAD,
                      timestamp: currentDate,
                      notes: 'File uploaded successfully'
                    },
                    {
                      status: AppStatus.ANALYZING_APK,
                      timestamp: currentDate,
                      notes: 'Analysis started after user login'
                    }
                  ],
                  appType: 'aab'
                });
                
                // Also update the analysis document to associate it with this user
                if (analysisDoc) {
                  await updateDocument('apkAnalysisUIProgress', fileData.fileId, {
                    userId: user.uid,
                    userEmail: user.email,
                    status: 'processing' // Ensure analysis status is also updated
                  });
                }
                
                console.log(`Anonymous AAB (${fileData.fileId}) linked to user in Firestore with status ANALYZING_APK`);
                
                // Navigate to dashboard only if we just linked a previously anonymous file
                resetApkDropUI(); // Reset UI before navigating
                navigate('/dashboard');
              }
              
              // Remove APK data from session storage to prevent future redirects
              sessionStorage.removeItem('apkFileData');
              
            } catch (error) {
              console.error('Error linking anonymous APK to user:', error);
              // Remove APK data from session storage even on error
              sessionStorage.removeItem('apkFileData');
            }
          };
          
          // Update state with file data
          setFileId(fileData.fileId);
          setFileName(fileData.fileName);
          setFileSize(fileData.fileSize);
          setDownloadUrl(fileData.downloadUrl);
          setSuccess(true);
          
          // Execute the linking function
          linkAnonymousApkToUser();
        } else {
          // Check if this is a redirect after login without saved APK data
          const redirectPath = sessionStorage.getItem('redirectAfterLogin');
          if (redirectPath === '/dashboard') {
            // Clear the redirect path and navigate
            sessionStorage.removeItem('redirectAfterLogin');
            resetApkDropUI(); // Reset UI before navigating
            navigate('/dashboard');
          }
        }
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Restore file data from session storage on component mount - but only if not already viewed in dashboard
  useEffect(() => {
    const savedFileData = sessionStorage.getItem('apkFileData');
    if (savedFileData) {
      const fileData = JSON.parse(savedFileData);
      
      // Don't restore file data if it was already viewed in dashboard
      if (!fileData.viewedInDashboard) {
        setFileId(fileData.fileId);
        setFileName(fileData.fileName);
        setFileSize(fileData.fileSize);
        setDownloadUrl(fileData.downloadUrl);
        setSuccess(true);
      }
    }
  }, []);

  // Listen for analysis updates if showAnalysis is true
  useEffect(() => {
    if (!showAnalysis || !fileId) return;

    setAnalysisLoading(true);
    setError(''); // Clear any previous errors
    
    // Set up a real-time listener for the analysis document
    const analysisRef = doc(db, 'apkAnalysisUIProgress', fileId);
    const unsubscribe = onSnapshot(
      analysisRef,
      (doc) => {
        setAnalysisLoading(false);
        if (doc.exists()) {
          const analysisData = { id: doc.id, ...doc.data() };
          setAnalysis(analysisData);
          
          // If analysis is complete and has results, offer to view detailed results
          if (analysisData.progress === 100 && analysisData.results) {
            // The "View Analysis Results" button will be shown by the Analysis component
          }
        } else {
          console.log(`Analysis document not found for fileId: ${fileId}`);
          setError('Analysis data not found. This might be a temporary issue.');
        }
      },
      (err) => {
        console.error('Error getting real-time updates:', err);
        setAnalysisLoading(false);
        setError(`Error loading analysis: ${err.message}`);
      }
    );

    // Clean up the subscription
    return () => unsubscribe();
  }, [showAnalysis, fileId]);

  // Cancel upload if component unmounts during upload
  useEffect(() => {
    return () => {
      if (uploadTaskRef.current) {
        uploadTaskRef.current.cancel();
      }
    };
  }, []);

  // Format file size to readable format
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleFile = async (file) => {
    // Reset analysis-related states when uploading a new file
    setShowAnalysis(false);
    setAnalysis(null);
    
    if (!file) {
      setError('No file selected');
      return;
    }
    
    // Get file extension
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    // Check if file type is allowed
    const isAllowed = await isFileTypeAllowed(fileExtension);
    
    if (isAllowed) {
      setSuccess(false);
      setError('');
      setUploading(true);
      setProgress(0);
      setDownloadUrl('');
      setFileId('');
      
      // Save file name and size
      setFileName(file.name);
      setFileSize(formatFileSize(file.size));

      // Generate a unique file ID for analysis reference
      const uniqueFileId = `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setFileId(uniqueFileId);

      // Real Firebase upload
      uploadTaskRef.current = uploadFile(
        file,
        (progressValue) => {
          setProgress(progressValue);
        },
        async (url) => {
          setUploading(false);
          setSuccess(true);
          setDownloadUrl(url);
          console.log('File uploaded successfully. Download URL:', url);
          
          // Save file data to session storage
          const fileData = {
            fileId: uniqueFileId,
            fileName: file.name,
            fileSize: formatFileSize(file.size),
            downloadUrl: url
          };
          sessionStorage.setItem('apkFileData', JSON.stringify(fileData));
          
          // Link APK file to user's applications collection if user is logged in
          const user = auth.currentUser;
          if (user) {
            try {
              console.log('Linking APK to user:', user.uid, 'File ID:', uniqueFileId);
              
              // Create current timestamp (using Date directly for immediate data)
              const currentDate = new Date();
              const fileSizeFormatted = formatFileSize(file.size);
              
              // Always create a new document. After analysis completes, the cloud function
              // will detect same Application ID (packageName) and merge versions automatically.
              const userAppDocId = await addDocument('userApps', {
                fileId: uniqueFileId,
                fileName: file.name,
                fileSize: fileSizeFormatted,
                downloadUrl: url,
                userId: user.uid,
                uploadedAt: currentDate,
                statusApp: AppStatus.ANALYZING_APK,
                lastStatusUpdate: currentDate,
                versionCount: 1,
                versionFileIds: [uniqueFileId],
                versions: [{
                  fileId: uniqueFileId,
                  fileSize: fileSizeFormatted,
                  downloadUrl: url,
                  uploadedAt: currentDate.toISOString(),
                  versionNumber: 1,
                  statusApp: AppStatus.ANALYZING_APK
                }],
                statusHistory: [
                  {
                    status: AppStatus.AWAITING_UPLOAD,
                    timestamp: currentDate,
                    notes: 'File uploaded successfully'
                  },
                  {
                    status: AppStatus.ANALYZING_APK,
                    timestamp: currentDate,
                    notes: 'Analysis started immediately after upload'
                  }
                ],
                appType: 'aab'
              });
              
              console.log('AAB file linked to user in Firestore with ID:', userAppDocId, 'Status: ANALYZING_APK');
              
              // Track file upload
              trackFileUpload(file.name, fileSizeFormatted);
              
              // Auto-trigger analysis immediately after upload
              // This calls startApkAnalysis cloud function which also sends email notification
              try {
                console.log('Auto-triggering analysis for fileId:', uniqueFileId);
                await triggerApkAnalysis(uniqueFileId);
                console.log('Analysis auto-triggered successfully');
              } catch (analysisError) {
                console.warn('Auto-trigger analysis failed (user can retry from dashboard):', analysisError.message);
              }

              // Navigate to dashboard after upload and analysis trigger
              setTimeout(() => {
                resetApkDropUI(); // Reset UI before navigating
                navigate('/dashboard');
              }, 1000); // Small delay to allow UI to show success state
              
            } catch (error) {
              console.error('Error linking AAB to user:', error);
              alert('אפליקציה הועלתה אך לא נוספה לרשימת האפליקציות שלך. אנא רענן את העמוד.');
            }
          } else {
            console.error('User not logged in when trying to link AAB');
          }
        },
        (errorMsg) => {
          setUploading(false);
          setError(`Upload error: ${errorMsg}`);
        },
        uniqueFileId // Pass the file ID to the storage service
      );
    } else {
      setSuccess(false);
      setError(`File type not supported. Allowed types: ${allowedFileTypes.map(t => t.extension).join(', ')}`);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleFileChange = (e) => {
    handleFile(e.target.files[0]);
  };

  const handleAnalyze = async () => {
    // Prevent multiple clicks
    if (startingAnalysis) {
      console.log('Analysis already starting, ignoring click');
      return;
    }
    
    // Only proceed if we have a fileId
    if (fileId) {
      try {
        // Show analysis starting state
        setStartingAnalysis(true);
        setError(''); // Clear any previous errors
        
        console.log(`Starting analysis with fileId: ${fileId}`);
        
        // If the user is logged in, update their userApps collection
        const user = auth.currentUser;
        if (user) {
          try {
            // Find the user app entry with this fileId
            const userApps = await queryDocuments('userApps', 'fileId', '==', fileId);
            
            if (userApps && userApps.length > 0) {
              // Update the existing entry using the appStatusService
              await updateAppStatus(
                userApps[0].id, 
                AppStatus.ANALYZING_APK, 
                'Analysis started from project upload page'
              );
              console.log('Updated existing userApps entry status to analyzing');
            } else {
              // Create a new entry if somehow it doesn't exist
              const timestamp = serverTimestamp();
              const currentDate = new Date();
              const userAppDocId = await addDocument('userApps', {
                fileId: fileId,
                fileName,
                downloadUrl,
                userId: user.uid,
                statusApp: AppStatus.ANALYZING_APK,
                uploadedAt: timestamp,
                lastUpdated: timestamp,
                lastStatusUpdate: timestamp,
                versionCount: 1,
                versionFileIds: [fileId],
                versions: [{
                  fileId: fileId,
                  fileSize: fileSize || '',
                  downloadUrl: downloadUrl,
                  uploadedAt: currentDate.toISOString(),
                  versionNumber: 1,
                  statusApp: AppStatus.ANALYZING_APK
                }],
                statusHistory: [
                  {
                    status: AppStatus.AWAITING_UPLOAD,
                    timestamp: timestamp,
                    notes: 'File uploaded successfully (from retry)'
                  },
                  {
                    status: AppStatus.ANALYZING_APK,
                    timestamp: timestamp,
                    notes: 'Analysis started'
                  }
                ],
                appType: 'aab'
              });
              console.log('Created new userApps entry with status analyzing');
            }
          } catch (updateError) {
            console.error('Error updating userApps status:', updateError);
            // Continue with analysis even if this update fails
          }
        }
        
        // Step 1: Submit the APK for analysis first to ensure the document exists
        try {
          // Make sure this call creates the document even if not logged in
          const result = await submitApkForAnalysis(fileId, fileName, downloadUrl);
          console.log('Analysis submitted:', result);
        } catch (submitError) {
          console.error('Error submitting analysis:', submitError);
          setError('Error submitting analysis. Please try again.');
          setStartingAnalysis(false);
          return; // Stop if we can't submit the analysis
        }
        
        // Step 2: Trigger the server-side analysis function
        try {
          const analysisResult = await triggerApkAnalysis(fileId);
          console.log('Analysis triggered successfully:', analysisResult);
          
          // Track analysis complete
          trackAnalysisComplete(fileId);
        } catch (triggerError) {
          console.error('Error triggering analysis:', triggerError);
          // If this fails, show an error but still allow viewing the analysis screen
          const errorMsg = triggerError.message?.includes('timed out') 
            ? 'Connection timed out. The analysis server may be slow. Please check back in a moment.'
            : 'Analysis started but there was an issue. You may need to retry.';
          setError(errorMsg);
        }
        
        // Reset analysis starting state
        setStartingAnalysis(false);
        
        // Show analysis section
        setShowAnalysis(true);
      } catch (error) {
        console.error('Error submitting analysis:', error);
        setStartingAnalysis(false);
        setError(`Analysis request failed: ${error.message}`);
      }
    } else {
      setError('No file selected. Please upload an AAB file first.');
    }
  };

  // Delete file handler
  const handleDeleteFile = async () => {
    setSuccess(false);
    setFileName('');
    setFileSize('');
    const fileIdToDelete = fileId; // Save for async operation below
    setFileId('');
    setDownloadUrl('');
    setShowAnalysis(false);
    setAnalysis(null);
    
    // Remove file data from session storage
    sessionStorage.removeItem('apkFileData');
    
    // Remove from user's applications collection if user is logged in
    const user = auth.currentUser;
    if (user && fileIdToDelete) {
      try {
        // Find the user app entry with this fileId
        const userApps = await queryDocuments('userApps', 'fileId', '==', fileIdToDelete);
        
        if (userApps && userApps.length > 0) {
          // We found the document - now delete it
          await deleteDocument('userApps', userApps[0].id);
          console.log(`Deleted userApp entry with fileId: ${fileIdToDelete}`);
        } else {
          console.log(`No userApp entry found with fileId: ${fileIdToDelete}`);
        }
        
        // Note: In a production app, we would also delete the file from storage
        // and the analysis entry from apkAnalysis collection.
      } catch (error) {
        console.error('Error removing file from user apps:', error);
      }
    }
  };

  // Cancel analysis and go back to upload
  const handleBackToUpload = () => {
    // First cancel the analysis by deleting the file
    handleDeleteFile();
    
    // Then close the analysis view
    setShowAnalysis(false);
  };

  // View analysis results - Navigate to the detailed results page
  const viewDetailedResults = () => {
    if (fileId && analysis?.progress === 100 && analysis?.results) {
      // Set a flag in sessionStorage to indicate user has viewed results in dashboard
      const savedFileData = sessionStorage.getItem('apkFileData');
      if (savedFileData) {
        const fileData = JSON.parse(savedFileData);
        // Add a flag to indicate results were viewed in dashboard
        fileData.viewedInDashboard = true;
        sessionStorage.setItem('apkFileData', JSON.stringify(fileData));
      }
      
      // Clear the local state using the new function
      resetApkDropUI();
      
      // Add a small delay to ensure all Firebase updates are complete
      setTimeout(() => {
        // Allow viewing results without login requirement
        navigate('/dashboard');
      }, 500);
    }
  };

  return (
    <section id="apk-drop-hero" className="relative w-full py-16 flex flex-col items-center justify-center overflow-hidden">
      {/* Video Background */}
      <video
        className="absolute top-0 left-0 w-full h-full object-cover z-0"
        src="/original-1.mp4"
        autoPlay
        muted
        loop
        playsInline
      />
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 z-10" />

      {/* Content */}
      <div className="relative z-20 text-center text-white px-4 w-full max-w-3xl mx-auto">
        {!showAnalysis ? (
          <>
            {!hideInstructions && (
              <p className="text-lg max-w-xl mx-auto text-gray-300 mb-8">
                Drag your {allowedFileTypes.length > 0 
                  ? allowedFileTypes[0].displayName || 'AAB' 
                  : 'AAB'} file below or click to upload. Get instant feedback and publish your app easily.
              </p>
            )}

            {/* Upload Area */}
            <div
              className={`w-full mx-auto border-4 border-dashed rounded-xl px-8 py-16 md:py-20 cursor-pointer transition-all duration-200
              ${dragOver ? 'border-blue-500 bg-blue-500/10 shadow-lg shadow-blue-500/20' : 'border-white/40 bg-white/5'}`}
              onClick={() => !success && inputRef.current.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept={allowedFileTypes.map(type => type.mimeType || type.extension).join(',')}
                hidden
                ref={inputRef}
                onChange={handleFileChange}
              />

              {uploading ? (
                <div className="w-full max-w-lg mx-auto">
                  <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="bg-blue-400 h-full transition-all duration-200"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p className="text-xl text-gray-300 mt-4">Uploading... {progress}%</p>
                </div>
              ) : success ? (
                <div className="animate-scalein">
                  <p className="text-green-300 font-semibold text-2xl">✔ File uploaded successfully</p>
                </div>
              ) : (
                <>
                  <p className="text-2xl md:text-3xl font-medium mb-3">
                    Drop your {allowedFileTypes.length > 0 
                      ? allowedFileTypes[0].displayName || 'AAB' 
                      : 'AAB'} file here or click to browse
                  </p>
                  <p className="text-lg text-gray-300 mt-3">
                    Supported: {allowedFileTypes.length > 0 
                      ? allowedFileTypes.map(t => t.extension).join(', ') 
                      : '.aab'} files only
                  </p>
                </>
              )}
            </div>

            {error && <p className="text-red-300 mt-6 text-lg">{error}</p>}
            
            {/* File display after successful upload */}
            {success && fileName && (
              <div className="mt-6 bg-gray-800/70 rounded-lg p-4 flex items-center justify-between animate-fade-in">
                <div className="flex items-center">
                  <div className="bg-blue-900 rounded w-12 h-12 flex items-center justify-center mr-4">
                    <span className="text-blue-400 text-xs font-mono">
                      {allowedFileTypes.length > 0 
                        ? allowedFileTypes[0].extension 
                        : '.AAB'}
                    </span>
                  </div>
                  <div className="text-left">
                    <p className="text-white font-medium truncate max-w-xs">{fileName}</p>
                    <p className="text-gray-400 text-sm">{fileSize}</p>
                  </div>
                </div>
                <button 
                  onClick={handleDeleteFile}
                  className="text-gray-400 hover:text-white p-2 transition-colors"
                  aria-label="Delete file"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            )}
            
            {/* Analyze Button - appears only after successful upload */}
            {success && fileId && (
              <div className="mt-8 animate-fade-in text-center">
                <Button 
                  onClick={handleAnalyze}
                  disabled={startingAnalysis}
                  className="mx-auto"
                  icon={startingAnalysis ? (
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : undefined}
                >
                  {startingAnalysis ? "Starting Analysis..." : "Analyze App"}
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="bg-black/30 p-4 md:p-6 rounded-xl backdrop-blur-sm">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold text-white">Analysis Progress</h2>
              <button 
                onClick={handleBackToUpload}
                className="text-blue-300 hover:text-white text-sm underline"
              >
                Back to Upload
              </button>
            </div>
            
            {analysisLoading ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-xl text-gray-300">Loading analysis data...</p>
              </div>
            ) : analysis ? (
              <div className="bg-white/5 rounded-lg p-4 md:p-6">
                {/* Pass fileId to Analysis component */}
                <AnalysisProgress 
                  inlineAnalysis={true} 
                  analysisData={analysis} 
                  onViewDetailedResults={viewDetailedResults}
                />
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-xl text-red-300">Analysis data not found. Please try again.</p>
                <Button 
                  onClick={handleBackToUpload}
                  className="mt-4"
                >
                  Return to Upload
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes scalein {
          from { transform: scale(0.5); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-scalein {
          animation: scalein 0.5s ease-out;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
        }
      `}</style>
    </section>
  );
} 